/**
 * A vitrine — grade de busca, navegação lateral e "adicionar ao pedido".
 *
 * A NAVEGAÇÃO LATERAL EM CASCATA (Categoria › Grupo › Subgrupo) é o pedido do
 * Lucas em 04/08, e a frase dele explica o porquê melhor do que eu explicaria:
 * *"esse foi o ponto de criarmos essa classificação, para facilitar"*. A
 * hierarquia existia no banco desde a migração e só era usada como texto de
 * busca; agora ela é o caminho.
 *
 * Um nível só aparece se tiver item — menu que abre lista vazia ensina a não
 * clicar. E a contagem da categoria pode ser MAIOR que a soma dos grupos dela:
 * item sem grupo conta na categoria e em nenhum grupo. É verdade, não erro.
 *
 * O cartão mostra o DISPONÍVEL, não o físico: para quem pede material, "quanto
 * posso pedir" é o único número honesto — o físico inclui o que já está
 * comprometido com outro romaneio.
 */

import { api, type BuscaDeItens, type ItemDaLista } from '../../api.js';
import { escapar, numero } from '../../comum/formato.js';
import { avisarBloqueante } from '../../comum/aviso.js';
import { obraAtual, adicionar, emConsulta, guardarPendente } from './estado.js';

export interface NoArvore {
  id: number;
  nome: string;
  itens: number;
  filhos: NoArvore[];
}

interface Opcoes {
  categoria?: number;
  grupo?: number;
  subgrupo?: number;
  busca?: string;
  /** A árvore completa, vinda de /api/pedidos/inicio. */
  arvore?: NoArvore[];
  /** Chamado quando a pessoa está em consulta e mandou adicionar algo. */
  aoPedirObra?: () => void;
}

type CampoOrdem = 'nome' | 'saldo' | 'codigo';

export function montarVitrine(raiz: HTMLElement, opcoes: Opcoes = {}): void {
  const estado = {
    busca: opcoes.busca ?? '',
    categoria: opcoes.categoria ?? null,
    grupo: opcoes.grupo ?? null,
    subgrupo: opcoes.subgrupo ?? null,
    pagina: 1,
    /**
     * A ordenação escolhida no topo (11/08, Lucas): nome, quantidade ou
     * código. Clicar de novo no critério ativo inverte a direção, como nos
     * cabeçalhos de tabela.
     */
    ordem: 'nome' as CampoOrdem,
    desc: false,
  };
  const arvore = opcoes.arvore ?? [];
  let ultimaBusca = 0;

  // A árvore de navegação saiu DAQUI: ela virou o menu lateral do módulo, que
  // fica sempre visível (pedido do Lucas — estabilidade visual). A vitrine é
  // só a grade: busca, cartões e paginação.
  raiz.innerHTML = `
    <div class="resumo" id="resumo"></div>
    <div class="vit-ordenar">
      <span>Ordenar por</span>
      <div class="chips" id="vit-ordem">
        <button class="chip" data-o="nome">Nome</button>
        <button class="chip" data-o="saldo">Quantidade</button>
        <button class="chip" data-o="codigo">Código</button>
      </div>
    </div>
    <div id="lista"><p class="carregando">Buscando…</p></div>
    <div class="paginacao" id="paginacao"></div>`;

  const resumo = raiz.querySelector<HTMLElement>('#resumo')!;
  const lista = raiz.querySelector<HTMLElement>('#lista')!;
  const paginacao = raiz.querySelector<HTMLElement>('#paginacao')!;

  const pintarOrdem = () => {
    raiz.querySelectorAll<HTMLElement>('#vit-ordem .chip').forEach((c) => {
      const ativo = c.dataset['o'] === estado.ordem;
      c.setAttribute('aria-pressed', String(ativo));
      const nome = c.dataset['o'] === 'nome' ? 'Nome' : c.dataset['o'] === 'saldo' ? 'Quantidade' : 'Código';
      c.textContent = ativo ? `${nome} ${estado.desc ? '↓' : '↑'}` : nome;
    });
  };
  raiz.querySelector('#vit-ordem')!.addEventListener('click', (e) => {
    const chip = (e.target as HTMLElement).closest<HTMLElement>('[data-o]');
    if (!chip) return;
    const campo = chip.dataset['o'] as CampoOrdem;
    if (estado.ordem === campo) {
      estado.desc = !estado.desc; // segundo clique inverte, como nas tabelas
    } else {
      estado.ordem = campo;
      // Direção de partida útil de cada critério: quantidade começa pelo
      // maior (quem ordena por estoque quer ver o que tem); as outras, A→Z/1→9.
      estado.desc = campo === 'saldo';
    }
    estado.pagina = 1;
    pintarOrdem();
    void buscar();
  });
  pintarOrdem();
  // O ampliar-foto é global (main.ts) — instalar aqui de novo era o bug do
  // "fechar três vezes": cada visita à tela empilhava mais um ouvinte.

  // ---------------------------------------------------------------- busca

  async function buscar() {
    const meu = ++ultimaBusca;
    // `comMovimento`: a vitrine mostra só o que o CD já teve alguma vez. Com o
    // catálogo do Sienge são 8,3 mil itens, e a maioria nunca passou pelo
    // galpão — vê-los aqui faria a vitrine parar de responder "temos isso?".
    //
    // `saldo=com` (11/08, Lucas): estoque zerado fica fora da vitrine. A ordem
    // é a escolhida nos chips do topo — nome, quantidade ou código.
    const p = new URLSearchParams({
      pagina: String(estado.pagina), porPagina: '24', comMovimento: '1',
      saldo: 'com', ordem: estado.desc ? `${estado.ordem}-desc` : estado.ordem,
    });
    if (estado.busca) p.set('busca', estado.busca);
    if (estado.categoria) p.set('categoria', String(estado.categoria));
    if (estado.grupo) p.set('grupo', String(estado.grupo));
    if (estado.subgrupo) p.set('subgrupo', String(estado.subgrupo));

    try {
      const r = await api.get<BuscaDeItens>(`/api/itens?${p}`);
      if (meu !== ultimaBusca) return;
      desenhar(r);
    } catch (erro) {
      if (meu !== ultimaBusca) return;
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui buscar.',
      )}</p>`;
    }
  }

  function desenhar(r: BuscaDeItens) {
    if (r.total === 0) {
      lista.innerHTML = `<p class="vazio">Nada encontrado${
        estado.busca ? ` para “${escapar(estado.busca)}”` : ' neste recorte'
      }.</p>`;
      resumo.textContent = '';
      paginacao.innerHTML = '';
      return;
    }
    resumo.innerHTML = `${caminho()}<span class="vit-total">${
      r.porSemelhanca
        ? `Nada exato para <b>${escapar(estado.busca)}</b> — ${r.total} parecido(s)`
        : `<b>${r.total}</b> item(ns)`
    }</span>`;

    lista.innerHTML = `<div class="grade">${r.itens.map(cartao).join('')}</div>`;
    lista.querySelectorAll<HTMLElement>('[data-add]').forEach((b) => {
      b.addEventListener('click', () => void aoAdicionar(b));
    });

    paginacao.innerHTML =
      r.paginas <= 1
        ? ''
        : `<button class="btn btn-claro" id="ant" ${r.pagina <= 1 ? 'disabled' : ''}>← Anterior</button>
           <span>Página ${r.pagina} de ${r.paginas}</span>
           <button class="btn btn-claro" id="prox" ${r.pagina >= r.paginas ? 'disabled' : ''}>Próxima →</button>`;
    paginacao.querySelector('#ant')?.addEventListener('click', () => {
      estado.pagina--;
      void buscar();
      raiz.scrollIntoView();
    });
    paginacao.querySelector('#prox')?.addEventListener('click', () => {
      estado.pagina++;
      void buscar();
      raiz.scrollIntoView();
    });
  }

  /** "Hidráulica › Conexões › Joelho" — onde a pessoa está, em uma linha. */
  function caminho(): string {
    const c = arvore.find((x) => x.id === estado.categoria);
    const g = c?.filhos.find((x) => x.id === estado.grupo);
    const s = g?.filhos.find((x) => x.id === estado.subgrupo);
    const partes = [c?.nome, g?.nome, s?.nome].filter(Boolean);
    return partes.length ? `<span class="vit-caminho">${escapar(partes.join(' › '))}</span>` : '';
  }

  function cartao(i: ItemDaLista & { disponivel?: number }): string {
    const foto = api.foto(i.foto);
    const mostrar = i.disponivel ?? i.saldo;
    const esgotado = mostrar <= 0;
    return `
      <article class="card">
        <div class="card-foto">
          ${
            i.miniatura
              ? `<img src="${i.miniatura}" alt="" ${foto ? `data-grande="${escapar(foto)}" data-fotos-de="${escapar(i.codigo)}"` : ''}
                      title="Clique para ampliar">`
              : foto
                ? `<img src="${escapar(foto)}" alt="" loading="lazy" data-grande="${escapar(foto)}" data-fotos-de="${escapar(i.codigo)}"
                        onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'sem',textContent:'foto indisponível'}))">`
                : '<span class="sem">sem foto</span>'
          }
        </div>
        <div class="card-corpo">
          <div class="card-codigo">${escapar(i.codigo)}</div>
          <div class="card-nome">${escapar(i.nome)}</div>
          ${i.medida ? `<div class="card-meta">${escapar(i.medida)}${i.medida2 ? ` · ${escapar(i.medida2)}` : ''}</div>` : ''}
          <div class="card-rodape">
            <span class="saldo ${esgotado ? 'zero' : ''}">${numero(mostrar)}
              <span class="card-un">${escapar(i.unidade ?? '')}</span></span>
          </div>
          <div class="card-pedir">
            <input type="number" min="1" step="any" value="1" data-qtd aria-label="Quantidade">
            <button class="btn" data-add="${escapar(i.codigo)}" ${
              esgotado ? 'title="Sem disponível — o pedido pode ficar aguardando compra"' : ''
            }>Adicionar</button>
          </div>
        </div>
      </article>`;
  }

  /**
   * Adiciona — ou, em consulta, guarda e manda escolher a obra.
   *
   * O caminho da consulta é o pedido do Lucas: quem está só olhando e resolve
   * pedir não pode perder o item. Ele fica guardado, a tela leva à escolha de
   * obra, e assim que a obra é escolhida o item entra no carrinho dela. Daí
   * em diante é o carrinho normal da obra.
   */
  async function aoAdicionar(botao: HTMLElement): Promise<void> {
    const cartaoEl = botao.closest('.card')!;
    const qtd = Number(cartaoEl.querySelector<HTMLInputElement>('[data-qtd]')!.value) || 1;
    const codigo = botao.dataset['add']!;

    if (emConsulta() || !obraAtual()) {
      guardarPendente(codigo, qtd);
      opcoes.aoPedirObra?.();
      return;
    }

    const obra = obraAtual()!;
    const original = botao.textContent;
    botao.setAttribute('disabled', '');
    try {
      await adicionar(obra.codigo, codigo, qtd);
      // Confirma NO LUGAR: sem isso a pessoa clica de novo achando que não foi
      // — e como somamos quantidade, o clique repetido dobraria o pedido.
      botao.textContent = '✓ no carrinho';
      setTimeout(() => {
        botao.textContent = original;
        botao.removeAttribute('disabled');
      }, 1200);
    } catch (erro) {
      botao.removeAttribute('disabled');
      avisarBloqueante(erro instanceof Error ? erro.message : 'Não consegui adicionar.');
    }
  }

  void buscar();
}
